import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import type * as Immutable from 'immutable';

import { Col, Row } from 'components/bootstrap';
import { Spinner } from 'components/common';
import history from 'util/History';
import Routes from 'routing/Routes';
import type { Report } from 'report/types';
import type { AvailableDashboard } from 'report/report-contents-page/useAvailableWidgets';
import useAvailableWidgets from 'report/report-contents-page/useAvailableWidgets';

import ReportContentsSelection from './ReportContentsSelection';
import ReportSummary from './ReportSummary';
import ReportContentsToolbar from './ReportContentsToolbar';
import ReportParameters from './ReportParameters';

import ReportsActions from '../ReportsActions';

type Props = {
  report: Report,
  reportLogo: string,
  action: 'create' | 'edit',
};

const _extractParameters = (report: Report, dashboards: Immutable.List<AvailableDashboard> | Array<AvailableDashboard>) => {
  const dashboardWidgets = report.widgets
    .map(({ dashboard_widget_id: widgetId, dashboard_id: dashboardId }) => {
      if (!dashboards) {
        return { widgets: undefined, searchId: undefined };
      }

      const dashboard = dashboards.find((d) => d.id === dashboardId);

      if (!dashboard) {
        throw new Error(`Report references dashboard <${dashboardId}> - not found!`);
      }

      const widgets = Object.values(dashboard.widgets).flat();

      return {
        widgets: widgets.find((widget) => widget.id === widgetId),
        searchId: dashboard.search_id,
      };
    });

  // TODO: Duplicate parameters need to be merged?
  const parameters = Object.fromEntries(
    dashboardWidgets.flatMap((w) => w.widgets.parameters)
      .map((parameter) => [parameter.name, parameter]),
  );

  const parameterSearchIds = Object.fromEntries(
    dashboardWidgets.flatMap(({ widgets, searchId }) => widgets.parameters.map(({ name }) => [name, searchId])),
  );

  return {
    parameters,
    parameterSearchIds,
  };
};

const formId = 'report-contents-selection-form';

const redirectToReportsPage = () => history.push(Routes.pluginRoute('REPORTS'));

const ReportContents = ({ action, report: initialReport, reportLogo: initialReportLogo }: Props) => {
  const [report, setReport] = useState(initialReport);
  const [reportLogo, setReportLogo] = useState(initialReportLogo);
  const [parameterValues, setParameterValues] = useState(report.parameterValues ?? {});
  const [dataTouched, setDataTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formAction = action === 'create' ? ReportsActions.create : ReportsActions.update;

  const { data: dashboards, isLoading } = useAvailableWidgets();

  const { parameters, parameterSearchIds } = useMemo(() => (dashboards
    ? _extractParameters(report, dashboards)
    : { parameters: {}, parameterSearchIds: {} }), [report, dashboards]);

  const requiredParameters = useMemo(() => Object.values(parameters).filter((parameter) => !parameter.optional && !parameterValues[parameter.name]), [parameters, parameterValues]);
  const requiresParameters = requiredParameters.length > 0;

  const _saveReport = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const resetState = () => setIsSubmitting(false);

    formAction(report, reportLogo, parameterValues).then(redirectToReportsPage, resetState);
  }, [formAction, parameterValues, report, reportLogo]);

  const _confirmNavigation = useCallback((event?: React.ChangeEvent) => {
    if (!dataTouched) {
      return;
    }

    // eslint-disable-next-line no-alert
    if (!window.confirm('Do you really want to abandon this page and lose your changes? This action cannot be undone.')) {
      event.preventDefault();
    }
  }, [dataTouched]);

  const _updateReport = useCallback((updatedReport: Report) => {
    setReport(updatedReport);
    setDataTouched(true);
  }, []);

  const _updateReportLogo = useCallback((nextReportLogo: string) => {
    setReportLogo(nextReportLogo);
    setDataTouched(true);
  }, []);

  const addParameterValue = useCallback((name: string, value: string) => {
    setParameterValues({ ...parameterValues, [name]: value });
  }, [parameterValues]);

  if (isLoading) {
    return <div><Spinner text="Loading report data, please wait..." /></div>;
  }

  return (
    <form id={formId} onSubmit={_saveReport}>
      <Row>
        <Col md={6}>
          <ReportContentsSelection formElementId={formId}
                                   report={report}
                                   reportLogo={reportLogo}
                                   dashboards={dashboards.toJS()}
                                   onReportChange={_updateReport}
                                   onReportLogoChange={_updateReportLogo}
                                   onCancel={_confirmNavigation}
                                   action={action}
                                   disabled={requiresParameters}
                                   isLoading={isSubmitting} />
        </Col>
        <Col md={5} mdOffset={1}>
          <ReportSummary report={report} reportLogo={reportLogo}>
            <ReportParameters parameters={Object.values(parameters)}
                              parameterValues={parameterValues}
                              onChange={addParameterValue}
                              parameterSearchIds={parameterSearchIds} />
            <ReportContentsToolbar action={action}
                                   disabled={requiresParameters}
                                   onCancel={_confirmNavigation}
                                   formElementId={formId}
                                   isLoading={isSubmitting} />
          </ReportSummary>
        </Col>
      </Row>
    </form>
  );
};

ReportContents.propTypes = {
  report: PropTypes.object,
  reportLogo: PropTypes.string,
  action: PropTypes.oneOf(['create', 'edit']),
};

ReportContents.defaultProps = {
  report: {
    title: '',
    subtitle: '',
    description: '',
    widgets: [],
    positions: [],
  },
  reportLogo: null,
  action: 'create',
};

export default ReportContents;
